import { useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Eye,
  FileText,
  Loader2,
  RotateCcw,
  Save,
  Send,
} from 'lucide-react'
import { ApiError } from '@/lib/api'
import { LegalHtml } from '@/components/legal/LegalHtml'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useNotify } from '@/components/shared/useNotify'
import {
  useAdminLegalDocument,
  usePreviewLegalContent,
  usePublishLegalDraft,
  useRestoreLegalRevision,
  useSaveLegalDraft,
  type AdminLegalDraft,
  type LegalContentWarning,
  type LegalContentWarningCode,
  type LegalContentWarningPath,
  type LegalDocumentKind,
  type LegalPreviewResult,
} from '@/hooks/useAdminLegalContent'

const TITLES: Record<LegalDocumentKind, string> = {
  privacy: 'Privacy Notice',
  terms: 'Terms of Use',
}

const WARNING_COPY: Record<LegalContentWarningCode, string> = {
  raw_html_ignored: 'Raw HTML was ignored.',
  disallowed_tag_removed: 'Unsupported markup was removed.',
  disallowed_attribute_removed: 'Unsupported attributes were removed.',
  unsafe_link_removed: 'A link was removed because it was not allowed.',
  previous_host_marker: 'External policy markers are not allowed.',
  empty_content: 'No publishable content remained after cleanup.',
}

const WARNING_PATH_COPY: Record<LegalContentWarningPath, string> = {
  markdown: 'Markdown',
  'markdown.image': 'Markdown image',
  html: 'HTML',
  'html.attribute': 'HTML attribute',
  href: 'Link',
}

const legalBodyClassName =
  'space-y-4 text-sm leading-6 text-foreground [&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_blockquote]:border-l [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:text-muted-foreground [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_h1]:text-xl [&_h1]:font-semibold [&_h2]:pt-3 [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:pt-2 [&_h3]:text-base [&_h3]:font-semibold [&_li]:ml-5 [&_ol]:list-decimal [&_p]:max-w-prose [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-4 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:p-2 [&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:p-2 [&_ul]:list-disc'

function formatDateTime(value: string | null | undefined): string {
  if (!value) return 'Not set'
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(value))
}

function formatDate(value: string | null | undefined): string {
  if (!value) return 'Publish time'
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(value))
}

function toInputDate(value: string | null | undefined): string {
  if (!value) return ''
  return new Date(value).toISOString().slice(0, 10)
}

function toIsoFromInputDate(value: string): string | null {
  if (!value) return null
  return `${value}T00:00:00.000Z`
}

function isPastDate(value: string): boolean {
  if (!value) return false
  return new Date(`${value}T00:00:00.000Z`).getTime() < Date.now()
}

function parseApiErrorBody(error: unknown):
  | { error?: string; warnings?: LegalContentWarning[] }
  | null {
  if (!(error instanceof ApiError)) return null
  try {
    // `body`, not `message`: since KAN-820 `message` is the extracted
    // human-readable reason, and this page needs the whole envelope
    // (`error` code plus sanitizer `warnings`).
    return JSON.parse(error.body) as {
      error?: string
      warnings?: LegalContentWarning[]
    }
  } catch {
    return null
  }
}

function warningsFromError(error: unknown): LegalContentWarning[] {
  return parseApiErrorBody(error)?.warnings ?? []
}

function describeError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    const body = parseApiErrorBody(error)
    if (error.status === 409 || body?.error === 'stale_draft') {
      return 'Another admin changed this draft. Reload before continuing.'
    }
    if (body?.error === 'legal_content_validation_failed') {
      return 'Sanitizer rejected this content.'
    }
    if (body?.error === 'invalid_change_note') {
      return 'Change note must be public-safe text.'
    }
    if (body?.error === 'past_effective_at_requires_confirmation') {
      return 'Confirm the past effective date before publishing.'
    }
    if (body?.error === 'future_effective_at_rejected') {
      return 'Future effective dates are not supported.'
    }
  }
  return fallback
}

function WarningList({ warnings }: { warnings: LegalContentWarning[] }) {
  if (warnings.length === 0) {
    return null
  }
  return (
    <div
      className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
      data-testid="legal-warning-panel"
    >
      <div className="flex items-center gap-2 font-medium">
        <AlertTriangle className="h-4 w-4" aria-hidden />
        Sanitizer warnings
      </div>
      <ul className="mt-2 space-y-1">
        {warnings.map((warning, index) => (
          <li key={`${warning.code}-${warning.path}-${index}`}>
            {WARNING_COPY[warning.code]}{' '}
            <span className="text-amber-800">
              {WARNING_PATH_COPY[warning.path]}.
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function StatusPanel({
  draft,
  publishedRevision,
  hasUnsavedChanges,
}: {
  draft: AdminLegalDraft | null
  publishedRevision: number | null
  hasUnsavedChanges: boolean
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4" aria-hidden />
          Status
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm sm:grid-cols-3">
        <div>
          <p className="text-muted-foreground">Draft</p>
          <div className="mt-1 flex items-center gap-2">
            <Badge variant={draft ? 'secondary' : 'outline'}>
              {draft ? `Version ${draft.version}` : 'No draft'}
            </Badge>
            {hasUnsavedChanges ? <Badge variant="outline">Unsaved</Badge> : null}
          </div>
        </div>
        <div>
          <p className="text-muted-foreground">Published</p>
          <div className="mt-1">
            <Badge variant={publishedRevision ? 'secondary' : 'outline'}>
              {publishedRevision ? `Revision ${publishedRevision}` : 'Not published'}
            </Badge>
          </div>
        </div>
        <div>
          <p className="text-muted-foreground">Draft updated</p>
          <p className="mt-1 font-medium">{formatDateTime(draft?.updatedAt)}</p>
        </div>
      </CardContent>
    </Card>
  )
}

export function LegalContentPage() {
  const { notify } = useNotify()
  const [kind, setKind] = useState<LegalDocumentKind>('privacy')
  const detailQuery = useAdminLegalDocument(kind)
  const previewMutation = usePreviewLegalContent(kind)
  const saveMutation = useSaveLegalDraft(kind)
  const publishMutation = usePublishLegalDraft(kind)
  const restoreMutation = useRestoreLegalRevision(kind)

  const [title, setTitle] = useState('')
  const [sourceMarkdown, setSourceMarkdown] = useState('')
  const [effectiveDate, setEffectiveDate] = useState('')
  const [preview, setPreview] = useState<LegalPreviewResult | null>(null)
  const [validationWarnings, setValidationWarnings] = useState<
    LegalContentWarning[]
  >([])
  const [changeNote, setChangeNote] = useState('')
  const [confirmPastEffectiveAt, setConfirmPastEffectiveAt] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [publishOpen, setPublishOpen] = useState(false)
  const [pendingKind, setPendingKind] = useState<LegalDocumentKind | null>(null)

  const detail = detailQuery.data
  const draft = detail?.draft ?? null
  const currentPublished = detail?.currentPublished ?? null
  const documentTitle = detail?.document.title ?? ''

  useEffect(() => {
    if (!detail) return
    const nextDraft = detail.draft
    setTitle(nextDraft?.title ?? detail.document.title)
    setSourceMarkdown(nextDraft?.sourceMarkdown ?? '')
    setEffectiveDate(toInputDate(nextDraft?.effectiveAt))
    setPreview(
      nextDraft
        ? {
            sanitizedHtml: nextDraft.sanitizedHtml,
            warnings: nextDraft.sanitizerWarnings,
          }
        : null,
    )
    setValidationWarnings([])
    setChangeNote('')
    setConfirmPastEffectiveAt(false)
    setMessage(null)
    setPublishOpen(false)
  }, [detail, kind])

  const hasUnsavedChanges = useMemo(() => {
    if (!draft) {
      return (
        title !== documentTitle ||
        Boolean(sourceMarkdown.trim() || effectiveDate)
      )
    }
    return (
      title !== draft.title ||
      sourceMarkdown !== draft.sourceMarkdown ||
      effectiveDate !== toInputDate(draft.effectiveAt)
    )
  }, [documentTitle, draft, effectiveDate, sourceMarkdown, title])

  const previewWarnings = preview?.warnings ?? validationWarnings
  const showPastConfirmation = isPastDate(effectiveDate)

  // Reason the "Review publish" action is blocked, or null when it may proceed.
  // Surfaced as a disabled state + tooltip so the user always knows *why*
  // publish isn't available, rather than a silent no-op on click.
  const publishBlockReason: string | null = !draft
    ? 'Save a draft before publishing.'
    : hasUnsavedChanges
      ? 'Save the draft before publishing.'
      : !changeNote.trim()
        ? 'Publish requires a public change note.'
        : null

  function handleKindChange(value: string) {
    const nextKind = value as LegalDocumentKind
    if (nextKind === kind) return
    if (hasUnsavedChanges) {
      setPendingKind(nextKind)
      return
    }
    setKind(nextKind)
  }

  function handleConfirmKindChange() {
    if (!pendingKind) return
    setKind(pendingKind)
    setPendingKind(null)
  }

  function handleCancelKindChange() {
    setPendingKind(null)
  }

  function handlePreview() {
    if (!sourceMarkdown.trim()) {
      setMessage('Markdown is required before previewing.')
      return
    }
    previewMutation.mutate(sourceMarkdown, {
      onSuccess: (result) => {
        setPreview(result)
        setValidationWarnings([])
        setMessage(null)
      },
      onError: (error) => {
        const warnings = warningsFromError(error)
        setValidationWarnings(warnings)
        setMessage(describeError(error, 'Could not preview legal content.'))
      },
    })
  }

  function handleSaveDraft() {
    if (!title.trim()) {
      setMessage('Title is required before saving.')
      return
    }
    if (!sourceMarkdown.trim()) {
      setMessage('Markdown is required before saving.')
      return
    }
    saveMutation.mutate(
      {
        title: title.trim(),
        sourceMarkdown,
        effectiveAt: toIsoFromInputDate(effectiveDate),
        version: draft?.version,
      },
      {
        onSuccess: ({ draft: savedDraft }) => {
          setPreview({
            sanitizedHtml: savedDraft.sanitizedHtml,
            warnings: savedDraft.sanitizerWarnings,
          })
          setValidationWarnings([])
          setMessage('Draft saved.')
          notify({ title: 'Draft saved', variant: 'success' })
        },
        onError: (error) => {
          const warnings = warningsFromError(error)
          if (warnings.length) {
            setValidationWarnings(warnings)
          }
          setMessage(describeError(error, 'Could not save draft.'))
        },
      },
    )
  }

  function handleReviewPublish() {
    // publishBlockReason drives the disabled state + tooltip on the button.
    // This is a defence-in-depth check in case the button is reached while
    // still blocked (e.g. focus + Enter before re-render).
    if (publishBlockReason) {
      setMessage(publishBlockReason)
      return
    }
    setMessage(null)
    setPublishOpen(true)
  }

  function handlePublish() {
    if (!draft) return
    publishMutation.mutate(
      {
        draftVersion: draft.version,
        changeNote: changeNote.trim(),
        effectiveAt: toIsoFromInputDate(effectiveDate),
        confirmPastEffectiveAt: showPastConfirmation
          ? confirmPastEffectiveAt
          : undefined,
      },
      {
        onSuccess: () => {
          setPublishOpen(false)
          setChangeNote('')
          setMessage('Draft published.')
          notify({ title: 'Legal content published', variant: 'success' })
        },
        onError: (error) => {
          setMessage(describeError(error, 'Could not publish draft.'))
        },
      },
    )
  }

  function handleRestore(revisionId: string) {
    restoreMutation.mutate(revisionId, {
      onSuccess: ({ draft: restoredDraft }) => {
        setTitle(restoredDraft.title)
        setSourceMarkdown(restoredDraft.sourceMarkdown)
        setEffectiveDate('')
        setPreview({
          sanitizedHtml: restoredDraft.sanitizedHtml,
          warnings: restoredDraft.sanitizerWarnings,
        })
        setValidationWarnings([])
        setChangeNote('')
        setConfirmPastEffectiveAt(false)
        setMessage('Revision restored as a new draft.')
        notify({ title: 'Revision restored', variant: 'success' })
      },
      onError: (error) => {
        setMessage(describeError(error, 'Could not restore revision.'))
      },
    })
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-foreground">
            Legal Content
          </h1>
          <Tabs
            value={kind}
            onValueChange={handleKindChange}
          >
            <TabsList aria-label="Legal document">
              <TabsTrigger value="privacy">Privacy Notice</TabsTrigger>
              <TabsTrigger value="terms">Terms of Use</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handlePreview}
            disabled={previewMutation.isPending || detailQuery.isLoading}
          >
            {previewMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Eye className="h-4 w-4" aria-hidden />
            )}
            Preview
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleSaveDraft}
            disabled={saveMutation.isPending || detailQuery.isLoading}
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Save className="h-4 w-4" aria-hidden />
            )}
            Save draft
          </Button>
          <Button
            type="button"
            onClick={handleReviewPublish}
            disabled={
              publishMutation.isPending ||
              detailQuery.isLoading ||
              publishBlockReason !== null
            }
            title={publishBlockReason ?? undefined}
          >
            <Send className="h-4 w-4" aria-hidden />
            Review publish
          </Button>
        </div>
      </div>

      {message ? (
        <div
          role="alert"
          className="rounded-md border border-border bg-muted/50 px-4 py-3 text-sm text-foreground"
        >
          {message}
        </div>
      ) : null}

      {detailQuery.isLoading ? (
        <div
          className="h-40 rounded-md border border-border bg-muted"
          aria-busy="true"
          aria-label="Loading legal content"
        />
      ) : detailQuery.isError || !detail ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          Could not load legal content.
        </div>
      ) : (
        <>
          <StatusPanel
            draft={draft}
            publishedRevision={currentPublished?.revisionNumber ?? null}
            hasUnsavedChanges={hasUnsavedChanges}
          />

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.8fr)]">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {TITLES[kind]} draft
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_180px]">
                  <div className="space-y-2">
                    <Label htmlFor="legal-title">Title</Label>
                    <Input
                      id="legal-title"
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="legal-effective-date">Effective date</Label>
                    <Input
                      id="legal-effective-date"
                      type="date"
                      value={effectiveDate}
                      onChange={(event) => {
                        setEffectiveDate(event.target.value)
                        setConfirmPastEffectiveAt(false)
                      }}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="legal-markdown">Markdown</Label>
                  <Textarea
                    id="legal-markdown"
                    className="min-h-[420px] font-mono text-sm"
                    value={sourceMarkdown}
                    onChange={(event) => setSourceMarkdown(event.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Eye className="h-4 w-4" aria-hidden />
                    Sanitized preview
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <WarningList warnings={previewWarnings} />
                  {preview?.sanitizedHtml ? (
                    <div className="rounded-md border border-border bg-background p-4">
                      <LegalHtml
                        html={preview.sanitizedHtml}
                        className={legalBodyClassName}
                      />
                    </div>
                  ) : (
                    <div className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
                      No preview available.
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Send className="h-4 w-4" aria-hidden />
                    Publish
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="legal-change-note">
                      Change note <span className="text-destructive">*</span>
                      <span className="font-normal text-muted-foreground">
                        (required to publish)
                      </span>
                    </Label>
                    <Textarea
                      id="legal-change-note"
                      className="min-h-24"
                      value={changeNote}
                      onChange={(event) => setChangeNote(event.target.value)}
                    />
                  </div>
                  {showPastConfirmation ? (
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={confirmPastEffectiveAt}
                        onChange={(event) =>
                          setConfirmPastEffectiveAt(event.target.checked)
                        }
                      />
                      Confirm past effective date
                    </label>
                  ) : null}
                  <Button
                    type="button"
                    onClick={handleReviewPublish}
                    disabled={publishMutation.isPending}
                  >
                    <Send className="h-4 w-4" aria-hidden />
                    Review publish
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="h-4 w-4" aria-hidden />
                Published history
              </CardTitle>
            </CardHeader>
            <CardContent>
              {detail.revisions.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No published revisions.
                </p>
              ) : (
                <div className="divide-y divide-border">
                  {detail.revisions.map((revision) => (
                    <div
                      key={revision.id}
                      className="flex flex-col gap-3 py-4 lg:flex-row lg:items-start lg:justify-between"
                    >
                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="secondary">
                            Revision {revision.revisionNumber}
                          </Badge>
                          {revision.id === detail.document.currentPublishedRevisionId ? (
                            <Badge variant="outline">
                              <CheckCircle2
                                className="mr-1 h-3 w-3"
                                aria-hidden
                              />
                              Current
                            </Badge>
                          ) : null}
                        </div>
                        <p className="font-medium">{revision.title}</p>
                        <p className="text-sm text-muted-foreground">
                          Published {formatDateTime(revision.publishedAt)}
                        </p>
                        <p className="max-w-3xl text-sm text-foreground">
                          {revision.changeNote}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleRestore(revision.id)}
                        disabled={restoreMutation.isPending}
                      >
                        <RotateCcw className="h-4 w-4" aria-hidden />
                        Restore to draft
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
              <DialogHeader>
                <DialogTitle>Publish {TITLES[kind]}</DialogTitle>
                <DialogDescription>
                  This revision becomes visible on the public legal page.
                </DialogDescription>
              </DialogHeader>
              {message ? (
                <div
                  role="alert"
                  className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive"
                >
                  {message}
                </div>
              ) : null}
              {draft ? (
                <div className="space-y-4">
                  <div className="grid gap-3 rounded-md border border-border p-4 text-sm sm:grid-cols-3">
                    <div>
                      <p className="text-muted-foreground">Title</p>
                      <p className="mt-1 font-medium">{draft.title}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Effective</p>
                      <p className="mt-1 font-medium">
                        {formatDate(toIsoFromInputDate(effectiveDate))}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Change note</p>
                      <p className="mt-1 font-medium">{changeNote.trim()}</p>
                    </div>
                  </div>
                  <div className="rounded-md border border-border bg-background p-4">
                    <LegalHtml
                      html={draft.sanitizedHtml}
                      className={legalBodyClassName}
                    />
                  </div>
                </div>
              ) : null}
              {showPastConfirmation ? (
                <label className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-4 py-3 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 accent-primary"
                    checked={confirmPastEffectiveAt}
                    onChange={(event) =>
                      setConfirmPastEffectiveAt(event.target.checked)
                    }
                  />
                  <span>
                    The effective date is in the past. Confirm to publish with
                    this effective date.
                  </span>
                </label>
              ) : null}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPublishOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handlePublish}
                  disabled={
                    publishMutation.isPending ||
                    (showPastConfirmation && !confirmPastEffectiveAt)
                  }
                  title={
                    showPastConfirmation && !confirmPastEffectiveAt
                      ? 'Confirm the past effective date before publishing'
                      : undefined
                  }
                >
                  {publishMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <Send className="h-4 w-4" aria-hidden />
                  )}
                  Publish
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog
            open={pendingKind !== null}
            onOpenChange={(open) => {
              if (!open) {
                handleCancelKindChange()
              }
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Discard unsaved edits?</DialogTitle>
                <DialogDescription>
                  Switching to{' '}
                  {pendingKind ? TITLES[pendingKind] : 'another document'} will
                  discard unsaved edits to {TITLES[kind]}.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelKindChange}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleConfirmKindChange}
                >
                  Discard edits
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  )
}
